import React, { useEffect, useState } from "react";

type Shot = {
  x: number; // en %
  y: number; // en %
  made: boolean;
};

type Zone = {
  name: string;
  color: string;
  labelX: number;
  labelY: number;
  d: string; // path d string
};

type Props = {
  shots: Shot[];
};

// ✅ Zones déplacées en dehors du composant
const zones: Zone[] = [
  {
    name: "Peinture",
    color: "rgba(34, 139, 34, 0.35)",
    labelX: 512,
    labelY: 250,
    d: "M 385 125 L 640 125 L 640 403 L 385 403 Z",
  },
  {
    name: "Mid-range gauche 0°",
    color: "rgba(78, 30, 97, 0.3)",
    labelX: 260,
    labelY: 250,
    d: "M 384 125 L 384 403 L 240 403 A 460 400 0 0 1 150 125 Z",
  },
  {
    name: "Mid-range droite 0°",
    color: "rgba(30, 97, 94, 0.3)",
    labelX: 760,
    labelY: 250,
    d: "M 640 125 L 640 403 L 784 403 A 460 400 0 0 0 874 125 Z",
  },
  {
    name: "Corner 3 pts gauche",
    color: "rgba(255, 0, 0, 0.25)",
    labelX: 120,
    labelY: 210,
    d: "M 95 125 L 95 295 L 165 295 A 115 220 0 0 1 148 125 Z",
  },
  {
    name: "Corner 3 pts droite",
    color: "rgba(255, 0, 0, 0.25)",
    labelX: 900,
    labelY: 210,
    d: "M 930 125 L 930 295 L 860 295 A 115 220 0 0 0 875 125 Z",
  },
  {
    name: "Top of the key",
    color: "rgba(0, 0, 255, 0.25)",
    labelX: 512,
    labelY: 565,
    d: "M 385 505 A 170 76 0 0 0 640 505 L 640 585 L 385 585 Z",
  },
  {
    name: "Aile 3 pts gauche",
    color: "rgba(93, 226, 60, 0.25)",
    labelX: 200,
    labelY: 470,
    d: "M 95 295 L 95 585 L 385 585 L 385 505 A 350 560 0 0 1 165 295 Z",
  },
  {
    name: "Aile 3 pts droite",
    color: "rgba(100, 235, 66, 0.25)",
    labelX: 830,
    labelY: 470,
    d: "M 930 295 L 930 585 L 640 585 L 640 505 A 350 560 0 0 0 860 295 Z",
  },
  {
    name: "Top arc 2 pts",
    color: "rgba(0, 128, 255, 0.25)",
    labelX: 512,
    labelY: 460,
    d: "M 240 403 A 325 270 0 0 0 785 403 L 855 403 L 185 403 Z",
  }
];

export const CourtZoneSVGOverlay = ({ shots }: Props) => {
  const [results, setResults] = useState<any[]>([]);

  useEffect(() => {
    const canvas = document.createElement("canvas");
    canvas.width = 1024;
    canvas.height = 1024;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const pxShots = shots.map((s) => ({
      x: (s.x / 100) * 1024,
      y: (s.y / 100) * 1024,
      made: s.made,
    }));

    const zoneResults = zones.map((zone) => {
      const path = new Path2D(zone.d);
      let made = 0;
      let attempted = 0;

      pxShots.forEach((shot) => {
        if (ctx.isPointInPath(path, shot.x, shot.y)) {
          attempted++;
          if (shot.made) made++;
        }
      });

      const percentage = attempted > 0 ? Math.round((made / attempted) * 100) : null;
      return {
        ...zone,
        path,
        made,
        attempted,
        percentage,
      };
    });

    setResults(zoneResults);
  }, [shots]);

  return (
    <div className="relative w-full max-w-3xl mx-auto aspect-square">
      <img
        src="/court.png"
        alt="Terrain"
        className="w-full h-full object-contain block select-none"
      />
      <svg
        viewBox="0 0 1024 1024"
        preserveAspectRatio="xMidYMid meet"
        className="absolute top-0 left-0 w-full h-full pointer-events-none"
      >
        {results.map((zone, i) => (
          <g key={i}>
            <path d={zone.d} fill={zone.color} />
            {zone.percentage !== null && (
              zone.name.includes("Corner") ? (
                <g>
                  <text
                    x={zone.labelX}
                    y={zone.labelY}
                    textAnchor="middle"
                    alignmentBaseline="baseline"
                    fill="white"
                    fontSize={16}
                    fontWeight="bold"
                  >
                    {zone.made}/{zone.attempted}
                  </text>
                  <text
                    x={zone.labelX}
                    y={zone.labelY + 18}
                    textAnchor="middle"
                    alignmentBaseline="baseline"
                    fill="white"
                    fontSize={16}
                    fontWeight="bold"
                  >
                    {zone.percentage}%
                  </text>
                </g>
              ) : (
                <text
                  x={zone.labelX}
                  y={zone.labelY}
                  textAnchor="middle"
                  alignmentBaseline="middle"
                  fill="white"
                  fontSize={18}
                  fontWeight="bold"
                >
                  {zone.percentage}% ({zone.made}/{zone.attempted})
                </text>
              )
            )}
          </g>
        ))}
      </svg>
    </div>
  );
};
